import React from 'react';
import BaseScadaNode from './BaseScadaNode';

export function BeltConveyorSVG({ config = {}, width = 120, height = 40, liveValues = {} }) {
    const running = parseFloat(liveValues.state ?? 1) >= parseFloat(config.threshold ?? 0.5);
    const hasError = liveValues.error === 1 || liveValues.error === true ||
        liveValues.error === '1' || liveValues.error === 'true';
    const statusColor = hasError ? '#EF4444' : running ? '#22C55E' : '#64748B';

    const cy = height / 2;
    const pulleyR = Math.min(height * 0.35, 12);
    const leftCX = pulleyR + 3;
    const rightCX = width - pulleyR - 3;
    const beltTop = cy - pulleyR;
    const beltBot = cy + pulleyR;
    const beltColor = config.belt_color || '#475569';

    // Dash pattern for moving belt
    const beltLen = rightCX - leftCX;
    const dashLen = 8;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Left pulley */}
            <circle cx={leftCX} cy={cy} r={pulleyR}
                fill="#1E293B" stroke={statusColor} strokeWidth={2} />
            <circle cx={leftCX} cy={cy} r={pulleyR * 0.45} fill={statusColor} opacity={0.5} />

            {/* Right pulley */}
            <circle cx={rightCX} cy={cy} r={pulleyR}
                fill="#1E293B" stroke={statusColor} strokeWidth={2} />
            <circle cx={rightCX} cy={cy} r={pulleyR * 0.45} fill={statusColor} opacity={0.5} />

            {/* Belt top strand */}
            <line x1={leftCX} y1={beltTop} x2={rightCX} y2={beltTop}
                stroke={beltColor} strokeWidth={4} strokeLinecap="round"
                strokeDasharray={running ? `${dashLen} ${dashLen / 2}` : 'none'}>
                {running && (
                    <animate attributeName="stroke-dashoffset"
                        from="0" to={`-${dashLen * 1.5}`}
                        dur="0.6s" repeatCount="indefinite" />
                )}
            </line>

            {/* Belt bottom strand */}
            <line x1={leftCX} y1={beltBot} x2={rightCX} y2={beltBot}
                stroke={beltColor} strokeWidth={4} strokeLinecap="round"
                strokeDasharray={running ? `${dashLen} ${dashLen / 2}` : 'none'}>
                {running && (
                    <animate attributeName="stroke-dashoffset"
                        from={`-${dashLen * 1.5}`} to="0"
                        dur="0.6s" repeatCount="indefinite" />
                )}
            </line>

            {/* Belt carrier marks (cross-bars) */}
            {Array.from({ length: Math.floor(beltLen / 14) }, (_, i) => {
                const x = leftCX + 10 + i * 14;
                if (x > rightCX - 8) return null;
                return (
                    <line key={i} x1={x} y1={beltTop + 2} x2={x} y2={beltTop + 6}
                        stroke={statusColor} strokeWidth={1.5} opacity={0.5} />
                );
            })}

            {/* Queue / saturation overlay */}
            {(() => {
                const queueLevel = parseFloat(liveValues.queue_level ?? 0);
                if (queueLevel <= 0) return null;
                const qPct = Math.min(100, queueLevel) / 100;
                const qWidth = beltLen * qPct;
                // Color: green → yellow → red
                const qColor = queueLevel < 40 ? '#22C55E' : queueLevel < 70 ? '#F59E0B' : '#EF4444';
                const boxCount = Math.max(0, Math.floor(qPct * 6));
                const boxW = 8;
                const boxH = Math.max(4, pulleyR - 2);
                return (
                    <g>
                        {/* Fill bar on belt surface */}
                        <rect x={rightCX - qWidth} y={beltTop - 1}
                            width={qWidth} height={beltBot - beltTop + 2} rx={1}
                            fill={qColor} opacity={0.15} />
                        {/* Box silhouettes stacking from right */}
                        {Array.from({ length: boxCount }, (_, i) => {
                            const bx = rightCX - 10 - i * (boxW + 4);
                            if (bx < leftCX + 8) return null;
                            return (
                                <rect key={`q${i}`} x={bx} y={beltTop - boxH - 1}
                                    width={boxW} height={boxH} rx={1}
                                    fill={qColor} opacity={0.5 + i * 0.05}
                                    stroke={qColor} strokeWidth={0.5} />
                            );
                        })}
                    </g>
                );
            })()}

            {/* Status dot */}
            <circle cx={leftCX + 14} cy={beltTop - 5} r={4}
                fill={statusColor} stroke="#0F172A" strokeWidth={1} />

            {/* Error flash overlay */}
            {hasError && (
                <rect x={1} y={1} width={width - 2} height={height - 2} rx={3}
                    fill="none" stroke="#EF4444" strokeWidth={2}>
                    <animate attributeName="stroke-opacity" values="1;0;1" dur="0.6s" repeatCount="indefinite" />
                </rect>
            )}
        </svg>
    );
}

export default function BeltConveyorNode(props) {
    const { data } = props;
    const w = data?.width || 120;
    const h = data?.height || 40;
    return (
        <BaseScadaNode {...props}>
            <BeltConveyorSVG config={data?.config} width={w} height={h} liveValues={data?.liveValues || {}} />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', marginTop: 2,
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
